import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "cookie_consent";

export type CookieConsent = "all" | "essential" | null;

export function getCookieConsent(): CookieConsent {
  try {
    return (localStorage.getItem(STORAGE_KEY) as CookieConsent) ?? null;
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  const accept = (choice: "all" | "essential") => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch { /* ignore */ }
    setVisible(false);

    const granted = choice === "all";

    // Meta Pixel consent (grant on "all", revoke on "Só essenciais")
    if (typeof window !== "undefined" && (window as { fbq?: unknown }).fbq) {
      (window as { fbq: (...args: unknown[]) => void }).fbq("consent", granted ? "grant" : "revoke");
    }

    // Google Consent Mode v2. Default is granted (Brazil opt-out model), so a
    // "Só essenciais" choice downgrades to denied here.
    if (typeof window !== "undefined" && (window as { gtag?: unknown }).gtag) {
      (window as { gtag: (...args: unknown[]) => void }).gtag("consent", "update", {
        ad_storage: granted ? "granted" : "denied",
        ad_user_data: granted ? "granted" : "denied",
        ad_personalization: granted ? "granted" : "denied",
        analytics_storage: granted ? "granted" : "denied",
      });
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-2xl"
        >
          <div className="rounded-2xl border bg-card shadow-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Usamos cookies</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Utilizamos cookies analíticos e de marketing para melhorar sua experiência.
                  Consulte nossa{" "}
                  <Link to="/privacy" className="underline hover:text-primary">
                    Política de Privacidade
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => accept("essential")}
                className="text-xs"
              >
                Só essenciais
              </Button>
              <Button
                size="sm"
                onClick={() => accept("all")}
                className="text-xs"
              >
                Aceitar todos
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
